"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  useIsGateAllowed,
  isSectionHrefActive,
} from "@/components/Layouts/pageSections";
import { ISectionNavItem, SectionSideNav } from "./SectionSideNav";
import { useSpotUser } from "@/utils/spotUser";
import { EntryIcon } from "./SideNavEntry";
import { NoxusOnlyIcon } from "@/components/UI/NoxusOnlyIcon";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsGenieEnabled } from "@/lib/genie/useIsGenieEnabled";
import {
  BookUser,
  Boxes,
  ChartBar,
  KeyRound,
  Package,
  Sparkles,
  UserRound,
  Users,
  Wallet,
  HeartPulse,
  Puzzle,
  Activity,
  Building2,
  Gauge,
  Settings2,
  WandSparkles,
  ListTree,
} from "lucide-react";
import { IEntry, sectionEntry } from "./sidenavShared";
import {
  IPageSection,
  PAGE_SECTIONS,
  SectionId,
  SectionedPage,
} from "@/components/Layouts/pageSections";
import { canAccessAdminActions } from "@/utils/adminActionsAccess";
import { User } from "@/generated";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import { EnvService } from "@/services/Environment/Environment";

// A settings page's sections, as the pages inside its sidenav entry.
const tabPages = (page: SectionedPage): IEntry[] =>
  (PAGE_SECTIONS[page].sections as readonly IPageSection[]).map((section) =>
    sectionEntry(page, section.id as SectionId<typeof page>, { icon: null })
  );

export const getSettingsEntries: (
  isOnPrem: boolean,
  spotUser: User,
  genieEnabled: boolean
) => {
  account_entries: IEntry[];
  administration_entries: IEntry[];
  platform_entries: IEntry[];
  observability_entries: IEntry[];
  internal_entries: IEntry[];
} = (isOnPrem, spotUser, genieEnabled) => {
  const account_entries: IEntry[] = [
    {
      id: "profile",
      title: "User",
      icon: UserRound,
      href: "/settings/profile",
      children: tabPages("profile"),
    },
  ];

  const administration_entries: IEntry[] = [
    {
      id: "analytics",
      title: "Analytics",
      icon: ChartBar,
      href: "/settings/observability/analytics",
      requiredPermissions: [ORG_PERMISSIONS.ORG.ANALYTICS],
      level: "tenant" as const,
      children: tabPages("orgAnalytics"),
    },
    {
      id: "billing",
      title: "Billing",
      icon: Wallet,
      href: "/settings/administration/billing",
      requiredPermissions: [ORG_PERMISSIONS.ORG.BILLING],
      level: "tenant" as const,
      children: tabPages("billing"),
    },
    {
      id: "users",
      title: "Users",
      icon: Users,
      href: "/settings/administration/users",
      requiredPermissions: [ORG_PERMISSIONS.USERS.READ],
      level: "tenant" as const,
    },
    {
      id: "workspaces",
      title: "Workspaces",
      icon: Boxes,
      href: "/settings/administration/workspaces",
      requiredPermissions: [ORG_PERMISSIONS.WORKSPACE.READ],
      level: "tenant" as const,
    },
    {
      id: "roles",
      title: "Roles",
      icon: BookUser,
      href: "/settings/administration/roles",
      children: tabPages("roles"),
      requiredPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
      level: "tenant" as const,
    },
    {
      id: "models",
      title: "Models",
      icon: Sparkles,
      href: "/settings/platform/models",
      requiredPermissions: [ORG_PERMISSIONS.PROVIDERS.MANAGE],
      level: "tenant" as const,
      children: tabPages("models"),
    },
    ...(genieEnabled
      ? [
          {
            id: "genie",
            title: "Genie",
            icon: WandSparkles,
            href: "/settings/platform/genie",
            adminOnly: true,
            noxusOnly: true,
          },
        ]
      : []),
  ];

  const platform_entries: IEntry[] = [
    ...(spotUser.is_admin
      ? [
          {
            id: "admin-authentication",
            title: "Identity & access",
            icon: KeyRound,
            href: "/settings/platform/authentication",
            adminOnly: true,
            children: tabPages("authentication"),
          },
          {
            id: "admin-integrations",
            title: "Integrations",
            icon: Puzzle,
            href: "/settings/platform/integrations",
            adminOnly: true,
            children: tabPages("platformIntegrations"),
          },
        ]
      : []),
    ...(spotUser.is_admin && EnvService.getVar("PLUGINS_ENABLED") === "true"
      ? [
          {
            id: "plugins",
            title: "Plugins",
            icon: Package,
            href: "/settings/organization/plugins",
            requiredPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
            level: "tenant" as const,
          },
        ]
      : []),
    ...(spotUser.is_admin
      ? [
          {
            id: "admin-rate-limits",
            title: "Rate limits",
            icon: Gauge,
            href: "/settings/platform/rate-limits",
            adminOnly: true,
          },
          {
            id: "admin-platform-status",
            title: "Status",
            icon: HeartPulse,
            href: "/settings/observability/platform-status",
            adminOnly: true,
          },
          {
            id: "admin-system",
            title: "System",
            icon: Settings2,
            href: "/settings/platform/system",
            adminOnly: true,
            children: tabPages("system"),
          },
        ]
      : []),
  ];

  const observability_entries: IEntry[] = [];

  const internal_entries: IEntry[] = spotUser.is_admin
    ? [
        ...(canAccessAdminActions(spotUser.email)
          ? [
              {
                id: "admin-actions",
                title: "Admin tools",
                icon: Activity,
                href: "/settings/internal/admin-actions",
                adminOnly: true,
                noxusOnly: true,
                children: tabPages("adminTools"),
              },
            ]
          : []),
        ...(!isOnPrem
          ? [
              {
                id: "admin-customers",
                title: "Customers",
                icon: Building2,
                href: "/settings/internal/customers",
                adminOnly: true,
                noxusOnly: true,
              },
            ]
          : [
              {
                id: "admin-tenants",
                title: "Tenants",
                icon: Building2,
                href: "/settings/internal/onprem-tenants",
                adminOnly: true,
                noxusOnly: true,
              },
            ]),
        {
          id: "admin-spec",
          title: "Feature list",
          icon: ListTree,
          href: "/settings/internal/spec",
          adminOnly: true,
          noxusOnly: true,
        },
      ]
    : [];

  return {
    account_entries,
    administration_entries,
    platform_entries,
    observability_entries,
    internal_entries,
  };
};

export const SettingsSideNav = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isOnPrem, spotUser } = useSpotUser();
  const { isLoading } = usePermissions();
  const genieEnabled = useIsGenieEnabled();

  const {
    account_entries,
    administration_entries,
    platform_entries,
    observability_entries,
    internal_entries,
  } = getSettingsEntries(isOnPrem, spotUser, genieEnabled);

  const isEntryAllowed = useIsGateAllowed();

  const isHrefActive = (href: string | undefined) => {
    if (href?.includes("?tab=")) {
      return isSectionHrefActive(href, pathname, searchParams?.get("tab"));
    }
    return href === "/" ? pathname === "/" : !!pathname?.includes(href ?? "");
  };

  // The entries the user may open; an expandable one is kept only while one
  // of its pages is allowed.
  const items = (entries: IEntry[]): ISectionNavItem[] =>
    entries.filter(isEntryAllowed).flatMap((entry): ISectionNavItem[] => {
      const icon = entry.noxusOnly ? (
        <NoxusOnlyIcon size="sm">
          <EntryIcon icon={entry.icon} />
        </NoxusOnlyIcon>
      ) : (
        entry.icon
      );
      if (!entry.children)
        return [
          {
            id: entry.id,
            title: entry.title,
            icon,
            isActive: isHrefActive(entry.href),
            href: entry.href ?? "#",
          },
        ];
      const children = entry.children.filter(isEntryAllowed);
      return children.length > 0
        ? [
            {
              id: entry.id,
              title: entry.title,
              icon,
              children: children.map((child) => ({
                id: child.id,
                title: child.title,
                isActive: isHrefActive(child.href),
                href: child.href ?? "#",
              })),
            },
          ]
        : [];
    });

  if (isLoading) return null;

  return (
    <SectionSideNav
      title="Settings"
      onBack={() => router.push("/")}
      testIdPrefix="settings"
      groups={[
        { label: "Account", items: items(account_entries) },
        { label: "Administration", items: items(administration_entries) },
        { label: "Platform", items: items(platform_entries) },
        { label: "Observability", items: items(observability_entries) },
        { label: "Internal (only Noxus)", items: items(internal_entries) },
      ]}
    />
  );
};
